// src/lib/types.ts
import { z } from 'zod';
import type { Timestamp as FirestoreTimestamp } from 'firebase/firestore';
import type { serverTimestamp } from 'firebase/firestore';

export interface ChatMessage {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: number | FirestoreTimestamp | ReturnType<typeof serverTimestamp>;
  userId?: string; // For user messages, this would be the user's UID
  isTyping?: boolean; // For AI typing indicator
  isSystemMessage?: boolean; // To identify system messages
  onClickAction?: () => void; // For system messages with actions
}

export const NONE_OPTION_VALUE = "__NONE__";

export type CharacterVisibility = 'public' | 'private';

export type LanguageLocale =
  | 'en' | 'es' | 'fr' | 'de' | 'ja' | 'it' | 'pt' | 'ru' | 'zh' | 'ar'
  | 'bn' | 'bg' | 'ca' | 'hr' | 'cs' | 'da' | 'nl' | 'et' | 'fa' | 'fi'
  | 'el' | 'gu' | 'he' | 'hi' | 'hu' | 'id' | 'kn' | 'ko' | 'lv' | 'lt'
  | 'ms' | 'ml' | 'mr' | 'no' | 'pl' | 'ro' | 'sr' | 'sk' | 'sl' | 'sw'
  | 'sv' | 'ta' | 'te' | 'th' | 'tr' | 'uk' | 'ur' | 'vi';

export const allCharacterLanguages: LanguageLocale[] = [
  'en', 'es', 'fr', 'de', 'ja', 'it', 'pt', 'ru', 'zh', 'ar',
  'bn', 'bg', 'ca', 'hr', 'cs', 'da', 'nl', 'et', 'fa', 'fi',
  'el', 'gu', 'he', 'hi', 'hu', 'id', 'kn', 'ko', 'lv', 'lt',
  'ms', 'ml', 'mr', 'no', 'pl', 'ro', 'sr', 'sk', 'sl', 'sw',
  'sv', 'ta', 'te', 'th', 'tr', 'uk', 'ur', 'vi'
];

export const FlirtyStyleSchema = z.enum([
    "Playful Flirt", "Shy Flirt", "Confident Charmer", "Intellectual Flirt", 
    "Mysterious Flirt", "Hopeless Romantic", "Sassy Flirt", 
    "Introverted Flirt", "Blunt Flirt"
]);
export type FlirtyStyle = z.infer<typeof FlirtyStyleSchema>;

export const SeductiveStyleSchema = z.boolean();
export type SeductiveStyle = z.infer<typeof SeductiveStyleSchema>;


export type CharacterFormValues = {
  name: string;
  username?: string;
  intro: string;
  greeting: string;
  permission: CharacterVisibility;
  tags?: { value: string }[];
  background?: string;
  personality?: string;
  avatarUrl?: string;
  language: LanguageLocale;
  interactions?: number;
  followerCount?: number;
  createdAt?: FirestoreTimestamp | number | null;
  id?: string;
};

export interface Character {
  id: string;
  username: string;
  name: string;
  searchName: string;
  imageUrl: string;
  imageHint?: string;
  description: string;
  tags: string[];
  interactions: number;
  creatorHandle: string;
  creatorUid: string;
  followerCount: number;
  greeting?: string;
  visibility: CharacterVisibility;
  background?: string;
  personality?: string;
  imagePromptForAvatar?: string;
  language?: LanguageLocale;
  seductiveStyle?: SeductiveStyle;
  createdAt: number;
  updatedAt: number;
  batchId?: string;
  isPrivateByCreator?: boolean;
  generationMetadata?: {
    textGenerationModel?: string;
    imageGenerationModel?: string;
  };
}

export const CharacterCreationDataSchema = z.object({
  name: z.string().min(1),
  username: z.string().min(1),
  searchName: z.string().min(1),
  imageUrl: z.string().url(),
  imageHint: z.string().optional(),
  description: z.string(),
  greeting: z.string().optional(),
  background: z.string().optional(),
  personality: z.string().optional(),
  seductiveStyle: SeductiveStyleSchema.optional(),
  visibility: z.enum(['public', 'private']),
  tags: z.array(z.string()),
  language: z.enum(allCharacterLanguages as [LanguageLocale, ...LanguageLocale[]]).optional(),
  creatorUid: z.string(),
  creatorHandle: z.string(),
  interactions: z.number().int().min(0),
  followerCount: z.number().int().min(0),
  imagePromptForAvatar: z.string().optional(),
  isPrivateByCreator: z.boolean().optional(),
  createdAt: z.any(),
  updatedAt: z.any(),
  id: z.string().optional(),
  batchId: z.string().optional(),
  generationMetadata: z.object({
    textGenerationModel: z.string().optional(),
    imageGenerationModel: z.string().optional(),
  }).optional(),
});
export type CharacterCreationData = z.infer<typeof CharacterCreationDataSchema>;

export type AuthModalMode = 'login' | 'signup';

export interface EnhanceTextInput {
  existingText: string;
  fieldType: 'intro' | 'greeting' | 'background' | 'personality' | 'imagePrompt' | 'chatContinuation';
  characterName?: string;
}

export interface RecentChatItem {
  characterId: string;
  characterUsername: string;
  characterName: string;
  characterAvatarUrl?: string;
  characterImageHint?: string;
  lastActivityTimestamp: number;
}

export interface AdminViewUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  createdAt: number;
  lastSignInTime?: number;
}

export interface GeneratedAvatarItem {
  id: string;
  storageUrl: string;
  userId: string;
  promptUsed?: string;
  styleUsed?: string;
  createdAt: FirestoreTimestamp | number;
  status?: 'active' | 'deleted';
  deletedAt?: any;
}

export interface AdminGenerationLog {
  id?: string;
  name: string;
  imagePromptForAvatar: string;
  adminUserId: string;
  createdAt: any;
  status?: 'active' | 'deleted';
  deletedAt?: any;
}

export interface ImageGenerationLog {
  id?: string;
  userId: string;
  prompt: string;
  fullPrompt: string;
  styleUsed: string | null;
  status: 'success' | 'failed';
  generatedImageUrl: string | null;
  failureReason?: string;
  createdAt: FirestoreTimestamp | ReturnType<typeof serverTimestamp> | number;
}


export const CHARACTER_DATA_UPDATED_EVENT = 'characterDataUpdated';

export const getStableGreetingId = (characterId: string, incognito: boolean): string => {
  return `greeting-${characterId}-${incognito ? 'incognito' : 'normal'}`;
};

export const createGreetingMessage = (
  character: Character,
  isIncognito: boolean,
  t: (key: string, replacements?: Record<string, string>) => string
): ChatMessage => ({
  id: getStableGreetingId(character.id, isIncognito),
  role: 'ai',
  content: character.greeting || t('defaultGreeting', { characterName: character.name }),
  timestamp: Date.now(),
  userId: 'ai-greeting',
  isTyping: false,
});

export const GenerateImageSuccessOutputSchema = z.object({
  status: z.literal('success'),
  imageDataUri: z
    .string()
    .describe("The generated image as a data URI. Expected format: 'data:image/png;base64,<encoded_data>'."),
});

export const GenerateImageRefusalOutputSchema = z.object({
  status: z.literal('refused'),
  reason: z.string().describe("The reason AI refused to generate the image."),
});

export const GenerateImageOutputSchema = z.union([
  GenerateImageSuccessOutputSchema,
  GenerateImageRefusalOutputSchema
]);
export type GenerateImageOutput = z.infer<typeof GenerateImageOutputSchema>;

export type GenerationResultItem =
  | { type: 'image'; url: string; id: string }
  | { type: 'refusal'; reason: string; id: string; originalPrompt: string; originalStyle: string | null };

export const PWASettingsSchema = z.object({
  name: z.string().min(1, "App name is required."),
  short_name: z.string().min(1, "Short name is required.").max(12, "Short name should be 12 characters or less."),
  theme_color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color code (e.g., #FFFFFF)."),
  background_color: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Must be a valid hex color code (e.g., #FFFFFF)."),
  icon_192_url: z.string().min(1, "An icon URL path is required."),
  icon_512_url: z.string().min(1, "An icon URL path is required."),
  staticCacheUrl: z.string().url().optional().nullable(),
  cacheLastGenerated: z.any().optional().nullable(),
});
export type PWASettings = z.infer<typeof PWASettingsSchema>;


export interface AdminGenerationJob {
    id: string;
    userId: string;
    prompt: string;
    status: 'running' | 'completed' | 'failed' | 'cancelled';
    totalAttempted: number;
    successfulCount: number;
    failedCount: number;
    startedAt: FirestoreTimestamp | number;
    completedAt?: FirestoreTimestamp | number;
    error?: string;
    currentActivity?: string;
}

export interface PixabayImage {
  id: number;
  pageURL: string;
  type: string;
  tags: string;
  previewURL: string;
  webformatURL: string;
  largeImageURL: string;
  user: string;
}
